/**
 * AcknowledgeEventReviewDialog — webhook 事件人工确认闭环（ADM-FIN-S010）
 *
 * 「确认闭环」走 POST /api/v1/finance/adapty-event-reviews/acknowledge
 * （financeApi.acknowledgeAdaptyEventReview），body 严格按 shared
 * AcknowledgeAdaptyEventReviewRequest：{ app_id, adapty_event_id, resolution_note(1..2000 非空白) }。
 * 本操作**不触发重放**（与 S009 归属后 reconcile 不同）。
 *
 * 快照只读行内字段，不反查源事件。OPEN + operator+ 才提交；已确认 / viewer 只读。
 * 成功 toast 用响应 acknowledged_at 区分首次写入 / 已 ACK no-op（后者不覆盖结论）。
 * 写端点无请求级幂等键 → financeApi 已设 replayOnUnauthorized:false。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { EventReviewQueueRow } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';
import {
  ADMISSION_KIND_ZH,
  ERROR_CODE_ZH,
  RESOLUTION_STATUS_ZH,
} from '../adapty-event-review-labels';

const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

function snap(value: string | null | undefined): string {
  return value && value.length > 0 ? value : '—';
}

export function AcknowledgeEventReviewDialog({
  row,
  canWrite,
  onClose,
}: {
  row: EventReviewQueueRow;
  canWrite: boolean;
  onClose: () => void;
}) {
  const [note, setNote] = useState(row.resolution_note ?? '');
  const open = row.resolution_status === 'OPEN';
  const editable = canWrite && open;

  const queryClient = useQueryClient();
  const submittedAtRef = useRef(0);
  const ackMutation = useMutation({
    mutationFn: () => {
      submittedAtRef.current = Date.now();
      return financeApi.acknowledgeAdaptyEventReview({
        app_id: row.app_id,
        adapty_event_id: row.adapty_event_id,
        resolution_note: note.trim(),
      });
    },
    onSuccess: (data) => {
      // 契约：已 ACK 仍 200，不覆盖 resolution_note。acknowledged_at 是首次确认时刻；
      // 早于本次提交（预留 2s 钟差）= no-op，不能说成「我刚写入」。
      const ackMs = Date.parse(data.acknowledged_at);
      const noOp = Number.isFinite(ackMs) && ackMs + 2000 < submittedAtRef.current;
      toast(
        noOp
          ? `事件 ${row.adapty_event_id} 此前已确认，本次结论未写入`
          : `事件 ${row.adapty_event_id} 已确认闭环 · 不触发重放`,
        { tone: noOp ? 'warn' : 'pos', icon: noOp ? 'IconAlert' : 'IconCheck' },
      );
      queryClient.invalidateQueries({ queryKey: ['adapty-event-reviews'] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '确认失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const noteTrimLen = note.trim().length;
  const canSubmit = editable && noteTrimLen > 0 && noteTrimLen <= 2000 && !ackMutation.isPending;

  return (
    <Dialog
      width={560}
      title={editable ? '确认事件闭环' : '事件快照'}
      subtitle={`${row.adapty_event_id} · ${row.appName}`}
      icon="IconCheck"
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            {editable
              ? '确认不触发事件重放 · 操作记入审计日志'
              : open
                ? '只读 · 确认需 operator+ 且 MFA'
                : '已确认 · 结论不可改写'}
          </span>
          <button className="btn" onClick={onClose}>
            {editable ? '取消' : '关闭'}
          </button>
          {editable && (
            <button
              className="btn btn-accent"
              disabled={!canSubmit}
              onClick={() => ackMutation.mutate()}
            >
              <Icons.IconCheck size={11} />
              确认闭环
            </button>
          )}
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="事件类型">
          <span className="mono" style={{ fontSize: 12.5 }}>
            {row.event_type}
          </span>
        </FormRow>
        <FormRow label="准入 / 错误码">
          <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
            <span
              className={`chip ${row.admission_kind === 'TERMINAL' ? 'chip-neg' : 'chip-warn'}`}
              style={{ fontSize: 10 }}
            >
              {ADMISSION_KIND_ZH[row.admission_kind]}
            </span>
            <span className="chip mono" style={{ fontSize: 10 }} title={row.error_code}>
              {ERROR_CODE_ZH[row.error_code] ?? row.error_code}
            </span>
          </div>
        </FormRow>
        <FormRow label="用户标识" hint="源行原值快照，可能是非法值，不按 UUID 反查">
          <span className="mono" style={{ fontSize: 12 }}>
            {snap(row.customer_user_id)}
          </span>
        </FormRow>
        <FormRow label="Profile">
          <span className="mono" style={{ fontSize: 12 }}>
            {snap(row.adapty_profile_id)}
          </span>
        </FormRow>
        <FormRow label="交易号">
          <span className="mono" style={{ fontSize: 12 }}>
            {snap(row.original_transaction_id)}
          </span>
        </FormRow>
        <FormRow label="渠道 / 产品">
          <span className="mono" style={{ fontSize: 12 }}>
            {snap(row.store)} · {snap(row.vendor_product_id)}
          </span>
        </FormRow>
        <FormRow label="缺失字段" hint="仅连续性字段缺失行非空">
          <span className="mono" style={{ fontSize: 12 }}>
            {row.missing_fields && row.missing_fields.length > 0
              ? row.missing_fields.join(', ')
              : '—'}
          </span>
        </FormRow>
        <FormRow label="时间">
          <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
            接收 {fmtDateTime(row.event_received_at)} · 进台账 {fmtDateTime(row.admitted_at)}
          </span>
        </FormRow>
        <FormRow label="状态">
          <span
            className={`chip ${row.resolution_status === 'ACKNOWLEDGED' ? 'chip-pos' : 'chip-warn'}`}
            style={{ fontSize: 10 }}
          >
            {RESOLUTION_STATUS_ZH[row.resolution_status]}
          </span>
        </FormRow>
        <FormRow
          label="处置结论"
          required={editable}
          hint={
            editable
              ? '审计必填 · 记录已知悉并完成处置 / 已判定无可恢复，≤ 2000 字'
              : '首次确认结论，本端点不提供改写面'
          }
        >
          {editable ? (
            <textarea
              className="input"
              rows={3}
              style={{ fontSize: 12, resize: 'vertical', width: '100%' }}
              placeholder="如：工单 #12345 已核对，customer_user_id 非法且无法映射，确认结案"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          ) : (
            <div style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{snap(row.resolution_note)}</div>
          )}
        </FormRow>
      </div>
    </Dialog>
  );
}
